import app from "flarum/forum/app";
import Component from "flarum/common/Component";
import Avatar from "flarum/common/components/Avatar";
import Button from "flarum/common/components/Button";
import classList from "flarum/common/utils/classList";
import { SidebarSkeleton } from "./Skeletons";
import ChannelFormModal from "./ChannelFormModal";
import { channelIcon } from "../utils/channelIcon";

/** An icon button in a section header. */

/**
 * Channel navigation: My Threads, Search, then the Channels and Direct Messages
 * sections — the same order and grouping as Discourse's chat sidebar.
 */
export default class ChatSidebar extends Component {
  view() {
    var _app$forum$attribute;
    const state = this.attrs.state;
    return m("div", {
      className: "ChatSidebar"
    }, m("div", {
      className: "ChatSidebar-top"
    }, this.quickLink("chat.threads", "fas fa-comments", "ramon-chat.forum.sidebar.my_threads"), this.quickLink("chat.bookmarks", "fas fa-bookmark", "ramon-chat.forum.sidebar.bookmarks"), this.quickLink("chat.search", "fas fa-magnifying-glass", "ramon-chat.forum.sidebar.search"), app.forum.attribute("canModerateChat") ? this.quickLink("chat.flags", "fas fa-flag", "ramon-chat.forum.sidebar.flags", Number((_app$forum$attribute = app.forum.attribute("chatOpenFlagsCount")) != null ? _app$forum$attribute : 0)) : null), m("div", {
      className: "ChatSidebar-scroll"
    }, state.channelsLoading && !state.channelsLoaded ? SidebarSkeleton() : m('[', null, this.section("ramon-chat.forum.sidebar.channels", state.categoryChannels(), [{
      // A magnifying glass, not a pencil: the action is "browse
      // channels", and a pencil promises editing.
      icon: "fas fa-magnifying-glass",
      title: app.translator.trans("ramon-chat.forum.sidebar.browse_channels", {}, true),
      action: () => m.route.set(app.route("chat.browse"))
    },
    // Same permission the browse page draws its own button from,
    // so the two agree about who may create a channel.
    ...(app.forum.attribute("canCreateChatChannel") ? [{
      icon: "fas fa-plus",
      title: app.translator.trans("ramon-chat.forum.sidebar.new_channel", {}, true),
      action: () => this.createChannel()
    }] : [])]), this.section("ramon-chat.forum.sidebar.direct_messages", state.directChannels(), []), state.channelsLoaded && state.channels.length === 0 ? m("div", {
      className: "ChatSidebar-empty"
    }, m("div", {
      className: "ChatSidebar-empty-icon",
      "aria-hidden": "true"
    }, m("i", {
      className: "fas fa-comments"
    })), m("p", null, app.translator.trans("ramon-chat.forum.sidebar.no_channels")), m("div", {
      className: "ChatSidebar-empty-actions"
    }, m(Button, {
      className: "Button Button--primary Button--block",
      icon: "fas fa-compass",
      onclick: () => m.route.set(app.route("chat.browse"))
    }, app.translator.trans("ramon-chat.forum.sidebar.browse_channels")), app.forum.attribute("canCreateChatChannel") ? m(Button, {
      className: "Button Button--block",
      icon: "fas fa-plus",
      onclick: () => this.createChannel()
    }, app.translator.trans("ramon-chat.forum.sidebar.new_channel")) : null)) : null)));
  }

  /**
   * One of the views above the channel list.
   *
   * Written once rather than three times, which is what makes the active state
   * affordable: three hand-rolled buttons had no way to say which view was open,
   * so the sidebar looked identical whether you were reading a channel, your
   * threads or your bookmarks.
   */
  quickLink(routeName, icon, key, badge) {
    var _m$route$get;
    // Read from the router rather than from a stored value: the route can also
    // change from browser back/forward, which no click handler observes.
    //
    // `?? ""` because `m.route.get()` is typed as a string and is not one until
    // the router has resolved a route. The sidebar also draws inside the drawer,
    // which mounts on its own root over whatever page is open, so it can render
    // before that — and an exception here takes down the entire Mithril tree,
    // not just this button.
    const current = (_m$route$get = m.route.get()) != null ? _m$route$get : "";
    const active = current.split("?")[0] === app.route(routeName);
    return m("button", {
      type: "button",
      className: classList("ChatSidebar-quickLink", {
        "ChatSidebar-quickLink--active": active
      }),
      "aria-current": active ? "page" : undefined,
      onclick: () => m.route.set(app.route(routeName))
    }, m("i", {
      className: "ChatSidebar-quickLink-icon ".concat(icon),
      "aria-hidden": "true"
    }), m("span", null, app.translator.trans(key)), badge ? m("span", {
      className: "ChatSidebar-quickLink-badge"
    }, badge > 99 ? "99+" : badge) : null);
  }

  /**
   * A labelled group of channels, with any affordances that belong to it.
   *
   * Takes a list rather than a single action because the channels header now
   * carries two — browse and create — and they are not alternatives: the create
   * button used to be rendered only in the empty state below, so it vanished the
   * moment you joined your first channel and the only remaining route to it was
   * through Browse.
   */
  section(labelKey, channels, actions) {
    // An empty section with no affordance is pure noise; hide it.
    if (channels.length === 0 && actions.length === 0) return null;
    return m("div", {
      className: "ChatSidebar-section"
    }, m("div", {
      className: "ChatSidebar-sectionHeader"
    }, m("span", null, app.translator.trans(labelKey)), actions.map(action => m("button", {
      type: "button",
      key: action.icon,
      className: "ChatSidebar-sectionHeader-action",
      title: action.title,
      "aria-label": action.title,
      onclick: action.action
    }, m("i", {
      className: action.icon,
      "aria-hidden": "true"
    })))), channels.map(channel => this.row(channel)));
  }
  row(channel) {
    var _channel$unreadMentio;
    const _this$attrs = this.attrs,
      state = _this$attrs.state,
      onSelect = _this$attrs.onSelect;
    const active = state.activeChannelId === Number(channel.id());
    const mentions = (_channel$unreadMentio = channel.unreadMentionsCount()) != null ? _channel$unreadMentio : 0;
    const unread = channel.hasUnread();
    return m("button", {
      type: "button",
      key: channel.id(),
      className: classList("ChatChannelRow", {
        "ChatChannelRow--active": active,
        "ChatChannelRow--muted": channel.isMuted()
      }),
      onclick: () => onSelect == null ? void 0 : onSelect(channel)
    }, channel.isDirect() ? this.avatars(channel) : m("span", {
      className: "ChatChannelRow-icon"
    }, channelIcon(channel)), m("span", {
      className: "ChatChannelRow-name"
    }, channel.displayName()), channel.isPrivate() ? m("i", {
      className: "ChatChannelRow-lock fas fa-lock",
      title: app.translator.trans("ramon-chat.forum.new_channel.private", {}, true),
      "aria-hidden": "true"
    }) : null, channel.isMuted() ? m("i", {
      className: "ChatChannelRow-mute fas fa-bell-slash",
      title: app.translator.trans("ramon-chat.forum.channel.muted", {}, true),
      "aria-hidden": "true"
    }) : null, mentions > 0 ? m("span", {
      className: "ChatChannelRow-badge ChatChannelRow-badge--mention"
    }, mentions > 99 ? "99+" : mentions) : unread ? m("span", {
      className: "ChatChannelRow-badge"
    }, channel.unreadCount()) : null);
  }

  /** Stacked avatars of the other participants, for a direct channel. */
  avatars(channel) {
    var _app$session$user;
    const actorId = (_app$session$user = app.session.user) == null ? void 0 : _app$session$user.id();

    // `hasMany` yields `false` when the relationship was not included.
    const participants = channel.participants() || [];
    const others = participants.filter(user => Boolean(user) && user.id() !== actorId).slice(0, 2);
    if (others.length === 0) {
      return m("span", {
        className: "ChatChannelRow-icon"
      }, m("i", {
        className: "fas fa-envelope",
        "aria-hidden": "true"
      }));
    }
    return m("span", {
      className: "ChatChannelRow-avatars"
    }, others.map(user => m(Avatar, {
      user: user,
      className: "Avatar"
    })));
  }
  createChannel() {
    app.modal.show(ChannelFormModal, {
      onSaved: channel => {
        this.attrs.state.setActiveChannel(Number(channel.id()));
        m.route.set(app.route("chat.channel", {
          id: channel.id()
        }));
      }
    });
  }
}
flarum.reg.add('ramon-chat', 'forum/components/ChatSidebar', ChatSidebar);